/**
 * Loads Google's Picker and opens it against an access token minted for the
 * signed-in tenant.
 *
 * The Picker is not a convenience here, it IS the grant. The connector asks
 * Google for `drive.file`, which is deliberately non-sensitive — it exposes
 * only files the user hands over explicitly, so the deployment never enters
 * Google's restricted-scope review. The direct consequence is that a
 * spreadsheet id the user did NOT pick through this dialog returns 404, which
 * is why there is no "paste a spreadsheet URL" path anywhere in this feature.
 *
 * Google's script is loaded lazily on first use rather than in index.html: the
 * overwhelming majority of sessions never open this dialog, and a connector
 * nobody is configuring should not cost every page load a third-party script.
 */
import { useCallback, useRef } from 'react';

const GAPI_SRC = 'https://apis.google.com/js/api.js';
const GIS_SRC = 'https://accounts.google.com/gsi/client';

/**
 * The Picker renders its own overlay straight into <body> at a z-index well
 * below MUI's modal layer (1300), so anything opened from inside a Dialog is
 * drawn UNDERNEATH the Dialog that opened it. The caller also hides its Dialog
 * while the Picker is up — that alone fixes the visible case — but this rule
 * stays as the floor, so the Picker can never end up buried by some other
 * overlay a future caller happens to have open.
 *
 * Injected once, lazily, alongside the scripts rather than living in
 * global.css: these two class names only exist while Google's own markup is
 * mounted, and the whole feature is loaded on demand.
 */
const PICKER_STYLE_ID = 'google-picker-zindex';

function ensurePickerStyles(): void {
  if (document.getElementById(PICKER_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = PICKER_STYLE_ID;
  style.textContent =
    '.picker-dialog-bg{z-index:2000!important}.picker-dialog{z-index:2001!important}';
  document.head.appendChild(style);
}

export interface PickedSpreadsheet {
  spreadsheetId: string;
  name: string;
}

/**
 * Deliberately NOT a `declare global` augmentation of `window.google`.
 * GoogleSignInButton.tsx already declares that property with the narrow shape
 * Google Identity Services needs for sign-in, and a second declaration of the
 * same property is a hard TypeScript error rather than a merge. The Picker's
 * surface is large, untyped and used in exactly one file, so it is reached
 * through these two local accessors instead — which also keeps the loose `any`
 * confined here rather than leaking into every file that touches `window`.
 */
function gapi(): any {
  return (window as unknown as { gapi?: any }).gapi;
}

function googleApi(): any {
  return (window as unknown as { google?: any }).google;
}

/** Resolves once the script has loaded. Concurrent calls for the same src
 *  share one <script> — React StrictMode double-invokes effects in dev, and
 *  two copies of gapi racing to initialise is a genuinely confusing failure. */
const loading = new Map<string, Promise<void>>();

function loadScript(src: string): Promise<void> {
  const existing = loading.get(src);
  if (existing) return existing;

  const promise = new Promise<void>((resolve, reject) => {
    const already = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
    if (already && already.dataset['loaded'] === 'true') {
      resolve();
      return;
    }
    const script = already || document.createElement('script');
    script.src = src;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', () => {
      script.dataset['loaded'] = 'true';
      resolve();
    });
    script.addEventListener('error', () => {
      loading.delete(src);
      reject(new Error(`Failed to load ${src}`));
    });
    if (!already) document.body.appendChild(script);
  });

  loading.set(src, promise);
  return promise;
}

function loadPickerModule(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (gapi()?.picker) {
      resolve();
      return;
    }
    gapi().load('picker', {
      callback: () => resolve(),
      onerror: () => reject(new Error('Picker failed to load')),
    });
  });
}

export function useGooglePicker() {
  const tokenRef = useRef<string | null>(null);
  /** The account the cached token belongs to. Reusing a token minted for a
   *  different account is exactly the mismatch this hint exists to prevent. */
  const tokenHintRef = useRef<string | undefined>(undefined);

  /**
   * Opens the Picker and resolves with the chosen spreadsheet, or `null` if
   * the user cancelled — cancelling is an ordinary outcome, not an error, so
   * callers get a value to branch on rather than an exception to catch.
   *
   * `accessToken` is minted browser-side by Google Identity Services for the
   * same client id the backend holds. It is scoped to drive.file, lives only
   * for this dialog, and is never sent to Baatly: the backend already has its
   * own long-lived grant from the OAuth callback, and this token exists purely
   * so the Picker UI can list the user's Drive.
   */
  const openPicker = useCallback(
    async (args: {
      clientId: string;
      apiKey: string;
      /** Email of the account the BACKEND holds a grant for. Passed to Google
       *  as a login hint so the Picker opens on that account. Without it a
       *  browser signed in to several Google accounts will happily let someone
       *  pick a file the server has no grant for, and the only symptom is a
       *  404 on the next step. */
      accountEmail?: string | undefined;
    }): Promise<PickedSpreadsheet | null> => {
      await Promise.all([loadScript(GAPI_SRC), loadScript(GIS_SRC)]);
      await loadPickerModule();
      ensurePickerStyles();

      const cachedForSameAccount =
        tokenRef.current && tokenHintRef.current === args.accountEmail;
      const accessToken = cachedForSameAccount
        ? (tokenRef.current as string)
        : await requestAccessToken(args.clientId, args.accountEmail);
      tokenRef.current = accessToken;
      tokenHintRef.current = args.accountEmail;

      return new Promise<PickedSpreadsheet | null>((resolve) => {
        const picker = googleApi().picker;
        const view = new picker.DocsView(picker.ViewId.SPREADSHEETS)
          // Shared-with-me sheets are exactly where a Meta Lead Ads export
          // usually lives — the marketing team owns the file and shares it —
          // so excluding them would miss the main use case.
          .setIncludeFolders(false)
          .setSelectFolderEnabled(false);

        const instance = new picker.PickerBuilder()
          .setAppId(args.clientId.split('-')[0])
          .setOAuthToken(accessToken)
          .setDeveloperKey(args.apiKey)
          .addView(view)
          .setTitle('Choose a spreadsheet')
          .setCallback((data: any) => {
            const action = data[picker.Response.ACTION];
            if (action === picker.Action.PICKED) {
              const doc = data[picker.Response.DOCUMENTS]?.[0];
              resolve(
                doc
                  ? {
                      spreadsheetId: doc[picker.Document.ID],
                      name: doc[picker.Document.NAME] || '',
                    }
                  : null
              );
              return;
            }
            // Anything terminal that ISN'T a pick resolves as a cancel, rather
            // than matching Action.CANCEL alone. The caller hides its own
            // dialog while this is open, so a promise that never settles would
            // strand the user looking at an empty page — and `loaded` is the
            // one non-terminal action the Picker emits, so excluding it is
            // enough to make everything else safe to treat as "done".
            if (action && action !== 'loaded') resolve(null);
          })
          .build();

        instance.setVisible(true);
      });
    },
    []
  );

  return { openPicker };
}

function requestAccessToken(clientId: string, accountEmail?: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const client = googleApi().accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: 'https://www.googleapis.com/auth/drive.file',
      // A hint, not a lock — Google still lets the user switch — but it makes
      // the right account the default instead of whichever one the browser
      // happens to have signed in first.
      ...(accountEmail ? { hint: accountEmail } : {}),
      callback: (response: { access_token?: string; error?: string }) => {
        if (response.access_token) resolve(response.access_token);
        else reject(new Error(response.error || 'Could not get Google access'));
      },
    });
    client.requestAccessToken();
  });
}
